import { SOCIAL } from "@/lib/site";

type Tone = "default" | "inverted";
type Size = "sm" | "md" | "lg";

const LinkedInGlyph = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);

const YoutubeGlyph = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const InstagramGlyph = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);

const XGlyph = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.292 19.494h2.039L6.486 3.24H4.298l13.311 17.407z" />
  </svg>
);

const items = [
  { href: SOCIAL.linkedin, label: "LinkedIn", a11y: "Arif Khan on LinkedIn", Glyph: LinkedInGlyph },
  { href: SOCIAL.youtube, label: "YouTube", a11y: "Arif Khan on YouTube", Glyph: YoutubeGlyph },
  { href: SOCIAL.instagram, label: "Instagram", a11y: "Arif Khan on Instagram", Glyph: InstagramGlyph },
  { href: SOCIAL.twitter, label: "X / Twitter", a11y: "Arif Khan on X (Twitter)", Glyph: XGlyph },
];

const sizeMap: Record<Size, { box: string; icon: string; gap: string; radius: string }> = {
  sm: { box: "h-8 w-8", icon: "h-[14px] w-[14px]", gap: "gap-2.5", radius: "rounded-[10px]" },
  md: { box: "h-10 w-10", icon: "h-[18px] w-[18px]", gap: "gap-3", radius: "rounded-xl" },
  lg: { box: "h-12 w-12", icon: "h-5 w-5", gap: "gap-3.5", radius: "rounded-2xl" },
};

export function SocialIcons({
  tone = "default",
  size = "md",
}: {
  tone?: Tone;
  size?: Size;
}) {
  const s = sizeMap[size];

  const surfaceRest =
    tone === "inverted"
      ? "border-white/15 bg-white/[0.06] text-white/85"
      : "border-white/60 bg-white/55 text-ink";

  const surfaceHover =
    tone === "inverted"
      ? "hover:border-white/40 hover:bg-white/[0.12] hover:text-white"
      : "hover:border-primary/40 hover:bg-white/80 hover:text-primary";

  // Subtle resting depth — thin highlight up top, faint ambient shadow below.
  const shadowRest =
    tone === "inverted"
      ? "shadow-[inset_0_1px_0_rgba(255,255,255,0.16),inset_0_-1px_0_rgba(0,0,0,0.22),0_1px_2px_rgba(0,0,0,0.22),0_6px_16px_-10px_rgba(0,0,0,0.55)]"
      : "shadow-[inset_0_1px_0_rgba(255,255,255,0.7),inset_0_-1px_0_rgba(15,23,42,0.05),0_1px_2px_rgba(15,23,42,0.05),0_6px_16px_-10px_rgba(15,23,42,0.16)]";

  // Hover ramps the ambient shadow about 1.6× and softens it — no harsh jump.
  const shadowHover =
    tone === "inverted"
      ? "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-1px_0_rgba(0,0,0,0.26),0_2px_4px_rgba(0,0,0,0.26),0_12px_24px_-12px_rgba(0,0,0,0.6)]"
      : "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.75),inset_0_-1px_0_rgba(15,23,42,0.07),0_2px_4px_rgba(15,23,42,0.07),0_12px_24px_-12px_rgba(15,23,42,0.2)]";

  return (
    <ul className={`flex flex-nowrap items-center ${s.gap}`}>
      {items.map(({ href, label, a11y, Glyph }) => (
        <li key={label} className="relative group/icon">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={a11y}
            title={label}
            style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
            className={[
              "relative inline-flex items-center justify-center border backdrop-blur-md outline-none",
              // Keeps the visual size but guarantees a 44x44 tap target on touch devices.
              "tap-target",
              // Uniform 2px lift across all sizes — premium ease, 260ms.
              "transition-[transform,box-shadow,background-color,border-color,color] duration-[260ms] will-change-transform",
              "hover:-translate-y-[2px] active:translate-y-0 active:duration-100 motion-reduce:hover:translate-y-0",
              "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              s.box,
              s.radius,
              surfaceRest,
              surfaceHover,
              shadowRest,
              shadowHover,
            ].join(" ")}
          >
            <Glyph className={s.icon} />
          </a>
          <span
            role="tooltip"
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-8 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-medium text-background opacity-0 shadow-md transition-opacity duration-200 group-hover/icon:opacity-100 group-focus-within/icon:opacity-100"
          >
            {label}
          </span>
        </li>
      ))}
    </ul>
  );
}
